import type { PlanGeoData } from '../proposition-c/types';
import { captureInputs } from './construction';
import { buildingsByIds, YEARS } from './engine';
import { FAMILY_KEYS, setsOf } from './model';
import type { Plan, Project, TechnicalPoint } from './types';

export type CalculationSection = 'trace' | 'density' | 'phasing';

export function migrateProject(project: Project): Project {
  if (project.technicalPoints) return project;
  const points = project.plans.flatMap((plan) => (plan.technicalPoints ?? []).map((point) => ({
    ...point, id: `${plan.id}:${point.id}`, originPlan: plan.name, originalStatus: point.status,
  })));
  const conflicts = new Set(points.filter((point) => point.status === 'validated' && points.some((other) =>
    other.id !== point.id && other.status === 'validated' && other.buildingId === point.buildingId)).map((point) => point.buildingId));
  return {
    ...project,
    technicalPoints: points.map((point) => conflicts.has(point.buildingId) && point.status === 'validated'
      ? { ...point, status: 'pending' as const } : point),
    plans: project.plans.map((plan) => ({
      ...plan, technicalPoints: undefined,
      buildingsConfirmed: plan.status === 'done',
      // Les anciens calculs globaux ne prouvent pas ces deux opérations locales.
      traceCalculated: false, densityCalculated: false,
      status: plan.status === 'running' ? 'draft' : plan.status,
      invalidations: plan.snapshot ? ['Ancien calcul : confirmer le tracé puis calculer la densité par section.'] : [],
      snapshot: plan.snapshot ? { ...plan.snapshot, results: structuredClone(plan.results) } : undefined,
    })),
  };
}

export function consumption(project: Project, plan: Plan) {
  const sets = setsOf(project, plan);
  return buildingsByIds(plan.buildingIds ?? []).reduce((total, b) => total + b.heat, 0)
    * (sets?.demande.consoCoef ?? 100) / 100 * (plan.energy === 'froid' ? 0.35 : 1);
}

export function segmentKm(geo: PlanGeoData | undefined, index: number): number {
  const geometry = geo?.pipes.features[index]?.geometry;
  if (!geometry || (geometry.type !== 'LineString' && geometry.type !== 'MultiLineString')) return 0;
  const lines = geometry.type === 'LineString' ? [geometry.coordinates] : geometry.coordinates;
  return lines.reduce((sum, line) => sum + line.slice(1).reduce((length, to, i) => {
    const from = line[i], rad = Math.PI / 180;
    const a = Math.sin((to[1] - from[1]) * rad / 2) ** 2
      + Math.cos(from[1] * rad) * Math.cos(to[1] * rad) * Math.sin((to[0] - from[0]) * rad / 2) ** 2;
    return length + 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }, 0), 0);
}

export function aggregate(project: Project, plan: Plan, geo?: PlanGeoData): Plan {
  if (!plan.results) return plan;
  const mwh = consumption(project, plan);
  const km = geo ? (plan.routePipeIndexes ?? []).reduce((sum, index) => sum + segmentKm(geo, index), 0) : plan.results.networkKm;
  return { ...plan, results: {
    ...plan.results, connectedBuildings: plan.buildingIds?.length ?? 0, connectedIds: [...(plan.buildingIds ?? [])],
    deliveredGWh: mwh / 1000, networkKm: km, density: km ? mwh / (km * 1000) : 0,
  } };
}

export function applyPlanChanges(project: Project, plan: Plan, changes: Partial<Plan>): Plan {
  const changed = (key: keyof Plan) => key in changes && JSON.stringify(plan[key]) !== JSON.stringify(changes[key]);
  const buildings = changed('buildingIds');
  const sets = changed('setIds');
  const route = changed('routePipeIndexes') || changed('routeMode');
  const phases = changed('buildingPhases') || changed('pipePhaseOverrides');
  const changedFamilies = FAMILY_KEYS.filter((family) => changes.setIds && changes.setIds[family] !== plan.setIds[family]);
  const fundamental = buildings || route || changedFamilies.some((f) => ['demande', 'importance', 'trace', 'pointsDepart'].includes(f));
  const reasons = [
    ...(plan.invalidations ?? []),
    ...(buildings ? ['Composition des bâtiments modifiée : confirmer les bâtiments puis relancer tracé, densité et phasage.'] : []),
    ...(route ? ['Tracé modifié : relancer tracé, densité et vérifier le phasage.'] : []),
    ...(sets ? [`Hypothèses remplacées (${changedFamilies.join(', ')}) : recalculer la section concernée et son aval.`] : []),
    ...(phases ? ['Dates modifiées : vérifier le phasage aval ; agrégats annuels mis à jour.'] : []),
  ];
  return aggregate(project, {
    ...plan, ...changes,
    ...(phases ? { phasingMode: plan.phasingMode === 'auto-simulation' || plan.phasingMode === 'mixed' ? 'mixed' as const : 'manual' as const } : {}),
    ...(fundamental ? { traceCalculated: false, densityCalculated: false } : {}),
    ...(buildings ? { buildingsConfirmed: changes.buildingsConfirmed ?? false } : {}),
    invalidations: [...new Set(reasons)],
    updatedAt: new Date().toLocaleString('fr-FR'),
    // Statut de calcul conservé ; l'obsolescence est dérivée, sans faux échec.
    configurationChanged: !!plan.snapshot && (fundamental || phases || sets || !!plan.configurationChanged),
  });
}

export function calculateSection(project: Project, plan: Plan, section: CalculationSection, geo?: PlanGeoData): Plan {
  const ids = plan.buildingIds ?? [];
  const sets = setsOf(project, plan);
  if (!sets) throw new Error('Hypothèses manquantes : choisissez un jeu dans chaque famille.');
  if (!geo) throw new Error('Géométries indisponibles : rechargez la copie locale avant ce calcul.');
  if (!plan.buildingsConfirmed || !ids.length) throw new Error('Confirmez au moins un bâtiment avant ce calcul.');
  let next = { ...plan, calculationError: undefined, calculationSection: section };
  if (section === 'trace') {
    const indexes = plan.routeMode === 'manual' ? plan.routePipeIndexes ?? []
      : Array.from({ length: Math.min(geo.pipes.features.length, Math.ceil(ids.length / 2)) }, (_, i) => i);
    if (!indexes.length) throw new Error('Aucun tronçon disponible : choisissez des tronçons manuels ou rechargez les géométries.');
    next = { ...next, routePipeIndexes: indexes, traceCalculated: true, densityCalculated: false,
      invalidations: ['Tracé simulé réalisé : calculer la densité et vérifier le phasage.'] };
  }
  if (section === 'density') {
    if (!plan.traceCalculated) throw new Error('Réalisez le tracé avant de calculer sa densité.');
    if (!(plan.routePipeIndexes ?? []).some((index) => segmentKm(geo, index) > 0)) throw new Error('Densité impossible : longueur géométrique nulle.');
    const indexes = [...new Set(plan.routePipeIndexes ?? [])].filter((index) => segmentKm(geo, index) > 0);
    next = { ...next, routePipeIndexes: indexes, densityCalculated: true, invalidations: [],
      densityCorrection: `Correction simulée : ${(plan.routePipeIndexes?.length ?? 0) - indexes.length} doublon(s) ou tronçon(s) nul(s) ignoré(s). Géométries source inchangées.` };
  }
  if (section === 'phasing') {
    if (!plan.traceCalculated) throw new Error('Réalisez le tracé avant le phasage automatique.');
    // Démonstration : ordre du plan, cadence clients/an et enveloppe annuelle MWh,
    // plafonnée par le développement. Pas d'optimiseur ni de moteur métier.
    const perYear = Math.max(1, Math.min(sets.phasage.clientsPerYear,
      Math.round(sets.phasage.mwhPerYear / Math.max(1, consumption(project, plan) / ids.length))));
    const span = Math.max(1, Math.ceil(sets.developpement.targetKm / Math.max(0.1, sets.phasage.kmPerYear)));
    next = { ...next, phasingMode: Object.keys(plan.pipePhaseOverrides ?? {}).length ? 'mixed' : 'auto-simulation',
      buildingPhases: Object.fromEntries(ids.map((id, i) =>
      [id, YEARS[Math.min(YEARS.length - 1, span - 1, Math.floor(i / perYear))]])), invalidations: [] };
  }
  const mwh = consumption(project, next);
  const km = (next.routePipeIndexes ?? []).reduce((total, index) => total + segmentKm(geo, index), 0);
  const snapshot = {
    computedAt: new Date().toLocaleString('fr-FR'),
    sets: Object.fromEntries(FAMILY_KEYS.map((f) => [f, { id: sets[f].id, version: sets[f].version }])) as NonNullable<Plan['snapshot']>['sets'],
    inputs: captureInputs(project, next, geo),
  };
  const result: Plan = {
    ...next, status: next.traceCalculated && next.densityCalculated ? 'done' : 'draft',
    progress: 100, configurationChanged: false, snapshot,
    archives: [...(plan.archives ?? []), ...(plan.snapshot ? [{ snapshot: structuredClone(plan.snapshot), results: structuredClone(plan.snapshot.results) }] : [])],
    results: {
      connectedBuildings: ids.length, connectedIds: [...ids], networkKm: km, deliveredGWh: mwh / 1000,
      erodedGWh: 0, density: km ? mwh / (km * 1000) : 0, durationYears: 0, triggered: 0,
    },
  };
  result.snapshot = { ...snapshot, results: structuredClone(result.results) };
  return result;
}

export function validateProjectPoints(points: TechnicalPoint[]) {
  const ids = points.filter((p) => p.status === 'validated').map((p) => p.buildingId);
  if (ids.some((id) => !id) || new Set(ids).size !== ids.length) throw new Error('Un seul local validé est autorisé par bâtiment.');
}
